/**
 * SoaControlRow
 *
 * A single row in the Statement of Applicability table.
 * Shows clause number, title, applicability toggle, justification, and AI confidence.
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as SwitchPrimitive from "@radix-ui/react-switch";
import { Bot, ChevronDown } from "lucide-react";
import { Tooltip } from "@/components/ui/Tooltip";
import { Badge } from "@/components/ui/primitives";
import { Textarea } from "@/components/ui/primitives";
import { cn, useTranslation } from "@/packages/utils";
import { springs } from "@/components/ui/motion";
import type { Id } from "@/packages/types";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SoaEntry {
  _id: Id<"statementOfApplicability">;
  clauseNumber: string;
  clauseTitle: string;
  clauseDescription: string;
  applicable: boolean;
  justification?: string | null;
  aiConfidence?: string | null;
  aiReasoning?: string | null;
  aiSuggested?: boolean;
  isAnnex: boolean;
}

interface SoaControlRowProps {
  entry: SoaEntry;
  onUpdate: (
    entryId: Id<"statementOfApplicability">,
    applicable: boolean,
    justification?: string,
  ) => void;
  disabled: boolean;
}

// ─── Confidence colors ──────────────────────────────────────────────────────

const CONFIDENCE_COLORS: Record<string, { bg: string; text: string }> = {
  high: { bg: "rgba(34,197,94,0.12)", text: "#16a34a" },
  medium: { bg: "rgba(234,179,8,0.12)", text: "#ca8a04" },
  low: { bg: "rgba(234,179,8,0.12)", text: "#ca8a04" },
};

// ─── Component ──────────────────────────────────────────────────────────────

export function SoaControlRow({
  entry,
  onUpdate,
  disabled,
}: SoaControlRowProps) {
  const { t } = useTranslation(["soa"]);
  const [expanded, setExpanded] = useState(
    !entry.applicable ||
      (entry.justification != null && entry.justification.length > 0),
  );
  const [justification, setJustification] = useState(entry.justification ?? "");

  const handleToggle = (checked: boolean) => {
    onUpdate(
      entry._id,
      checked,
      checked ? undefined : justification || undefined,
    );
  };

  const handleJustificationBlur = () => {
    if (justification !== (entry.justification ?? "")) {
      onUpdate(entry._id, entry.applicable, justification || undefined);
    }
  };

  const confidenceKey = entry.aiConfidence ?? "medium";
  const confidenceColors =
    CONFIDENCE_COLORS[confidenceKey] ?? CONFIDENCE_COLORS.medium;

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-card transition-colors duration-150",
        !entry.applicable && "border-l-amber-400",
        entry.applicable && "border-l-emerald-400",
      )}
      style={{ borderLeftWidth: 3 }}
    >
      {/* Main row */}
      <div className="flex items-center gap-3 px-3.5 py-2.5">
        {/* Clause number badge */}
        <span className="font-mono text-[11px] font-semibold tracking-tight shrink-0 min-w-[80px] text-secondary">
          {entry.clauseNumber}
        </span>

        {/* Title */}
        <span className="text-[13px] font-medium text-primary truncate flex-1">
          {entry.clauseTitle}
        </span>

        {/* AI suggested label */}
        {entry.aiSuggested && (
          <Tooltip content={entry.aiReasoning ?? ""}>
            <span className="flex items-center gap-1 text-[10px] font-medium text-secondary/60 shrink-0">
              <Bot size={12} />
              {t("soa:aiSuggested")}
            </span>
          </Tooltip>
        )}

        {/* AI confidence badge */}
        {entry.aiConfidence && (
          <Tooltip content={entry.aiReasoning ?? ""}>
            <span>
              <Badge
                color={confidenceColors}
                className="shrink-0 text-[10px] cursor-default"
              >
                {t(`soa:confidence.${confidenceKey}` as "soa:confidence.high")}
              </Badge>
            </span>
          </Tooltip>
        )}

        {/* Applicable toggle */}
        <SwitchPrimitive.Root
          checked={entry.applicable}
          onCheckedChange={handleToggle}
          disabled={disabled}
          className={cn(
            "relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors",
            entry.applicable
              ? "bg-emerald-500"
              : "bg-zinc-300 dark:bg-zinc-600",
            disabled && "opacity-50 cursor-not-allowed",
          )}
        >
          <SwitchPrimitive.Thumb
            className={cn(
              "block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform",
              entry.applicable ? "translate-x-[18px]" : "translate-x-[3px]",
            )}
          />
        </SwitchPrimitive.Root>

        {/* Expand button */}
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="p-1 rounded hover:bg-card-hover transition-colors"
        >
          <motion.span
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={springs.snappy}
            className="block"
          >
            <ChevronDown size={14} className="text-secondary" />
          </motion.span>
        </button>
      </div>

      {/* Expanded justification area */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="px-3.5 pb-3 pt-0">
              {entry.clauseDescription && (
                <p className="text-[12px] text-secondary/70 mb-2 leading-relaxed">
                  {entry.clauseDescription}
                </p>
              )}
              <Textarea
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                onBlur={handleJustificationBlur}
                disabled={disabled}
                placeholder={
                  entry.applicable
                    ? t("soa:justificationOptionalPlaceholder")
                    : t("soa:justificationPlaceholder")
                }
                className="text-[12px] min-h-[60px] resize-none"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
